import React, { useState, useContext } from 'react';
import { CartContext } from '../context/CartContext';
import ImageUpload from './ImageUpload';
import Button from './ui/Button';
import { useToast } from './ui/Toast';

const TYPE_LABELS = {
  BOTTOM: 'Botton',
  KEYCHAIN: 'Chaveiro',
  POLAROID: 'Polaroid',
  STICK: 'Sticker',
};

const ProductCard = ({ product }) => {
  const [customImage, setCustomImage] = useState(null);
  const [showUpload, setShowUpload] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [selectedSizeLabel, setSelectedSizeLabel] = useState(
    product.sizes?.find(s => s.label === 'M') || product.sizes?.[1] || product.sizes?.[0] || null
  );
  const { addToCart } = useContext(CartContext);
  const { push } = useToast();

  const handleAddToCart = () => {
    const sizeSuffix = selectedSizeLabel?.label ? ` (${selectedSizeLabel.label})` : '';

    if (!product.customizable) {
      // Não personalizável: vai direto ao carrinho com tamanho selecionado
      addToCart(product, null, { sizeLabel: selectedSizeLabel?.label });
      push(`${product.name}${sizeSuffix} adicionado ao carrinho`, 'success');
      return;
    }

    if (!customImage) {
      setUploadError('Envie uma imagem para personalizar este produto.');
      return;
    }
    setUploadError('');
    addToCart(product, customImage, { sizeLabel: selectedSizeLabel?.label });
    push(`${product.name}${sizeSuffix} adicionado ao carrinho`, 'success');
    setCustomImage(null);
    setShowUpload(false);
  };

  const hasSizes = product.sizes && product.sizes.length > 0;
  // Bottons personalizados usam o tamanho do produto; prontos, o escolhido.
  const sizeLabel = product.customizable ? product.sizeLabel : selectedSizeLabel?.label;
  const sizePromo = (product.sizePromotions || []).find(
    (item) => item.label === sizeLabel
  );
  // Promoção só é exibida nos personalizáveis; nos prontos, só o preço.
  const promoText = product.customizable ? sizePromo?.text : null;

  return (
    <article className="product-card">
      <div className="product-image">
        {product.image ? (
          <img
            src={product.image}
            alt={product.alt || product.name}
            className="product-image-content"
            loading="lazy"
          />
        ) : (
          <span className="product-placeholder">{product.icon || 'A'}</span>
        )}
        {promoText && <div className="promo-label">{promoText}</div>}
      </div>

      <div className="product-info">
        <div className="product-meta">
          <span className="flex flex-wrap items-center gap-1.5 min-w-0">
            {!product.customizable && (product.tags || []).map(tag => (
              <span
                key={tag.id}
                className="rounded-full bg-cocoa-100 px-1.5 py-0.5 text-[9px] font-medium text-cocoa-700 sm:text-[10px]"
              >
                {tag.name}
              </span>
            ))}
            {/* Todos os produtos atuais são bottons; o tipo fica oculto. */}
            {product.type !== 'BOTTOM' && (
              <span className="product-type">{TYPE_LABELS[product.type] || product.type}</span>
            )}
          </span>
          
          {/* Customizável indicador */}
          {product.customizable && (
            <span className="ml-2 text-[9px] sm:text-[10px] px-1.5 py-0.5 rounded-full bg-primary-100 text-primary-700 font-medium">
              Personalizável
            </span>
          )}
        </div>
        <div className="product-title-row">
          <h3>{product.name}</h3>
        </div>

        {!product.customizable && product.description && (
          <p className="product-description">{product.description}</p>
        )}

        {/* Seletor de tamanho inline quando há mais de um preço */}
        {hasSizes && (
          <div className="flex flex-wrap items-center gap-2 mb-2">
            {product.sizes.map(size => (
              <button
                key={size.label}
                type="button"
                onClick={() => setSelectedSizeLabel(size)}
                className={`text-[10px] sm:text-xs px-2 py-0.5 rounded-full border transition-colors font-medium
                  ${selectedSizeLabel?.label === size.label
                    ? 'bg-cocoa-800 text-cream-50 border-cocoa-800'
                    : 'border-cocoa-200 text-ink-muted hover:border-cocoa-400 bg-cream-50'}`}
              >
                {size.label} — R$ {Number(size.price).toFixed(2)}
              </button>
            ))}
          </div>
        )}

        <div className="product-price-row">
          <span className="product-price">
            {hasSizes
              ? `R$ ${Number(selectedSizeLabel?.price ?? product.sizes?.[0]?.price ?? product.price).toFixed(2)}`
              : `R$ ${product.price.toFixed(2)}`}
          </span>
        </div>

        {showUpload && product.customizable && (
          <div className="mb-3 sm:mb-4">
            <label className="upload-label">Imagem para Personalização *</label>
            {uploadError && <span className="upload-error" role="alert">{uploadError}</span>}
            <ImageUpload
              onImageSelect={(image) => {
                setCustomImage(image);
                if (image) setUploadError('');
              }}
              currentImage={customImage}
              productType={product.type}
              onAddToCart={handleAddToCart}
            />
          </div>
        )}

        <div className="product-actions">
          {product.available === false ? (
            <Button variant="secondary" className="w-full" disabled>
              Indisponível
            </Button>
          ) : !showUpload ? (
            <Button
              onClick={() => {
                if (product.customizable) {
                  setShowUpload(true);
                } else {
                  handleAddToCart();
                }
              }}
              className="w-full"
            >
              {product.customizable ? 'Personalizar' : 'Comprar'}
            </Button>
          ) : (
            <div className="product-action-group">
              <Button
                variant="secondary"
                className="w-full"
                onClick={() => {
                  setShowUpload(false);
                  setCustomImage(null);
                  setUploadError('');
                }}
              >
                Cancelar
              </Button>
              <Button onClick={handleAddToCart} className="w-full">
                Adicionar ao Carrinho
              </Button>
            </div>
          )}
        </div>
      </div>
    </article>
  );
};

export default ProductCard;
